import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardTypeEnum, CircularProgress, Difficulty, secondaryColors, StyledCheckIcon, Tooltip } from '@tryhackme/thm-ui-components';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { useGetUserQuery } from 'src/common/slices';
import { PathOutlineModal } from 'src/common/components/path-outline-modal';
import { Path } from 'src/common/interfaces/paths';
import { RoadmapVariant } from '../../roadmap.types';
import { StyledPathCardContainer, StyledPathCardContent, StyledPathTag, StyledCurrentPathTag, StyledProgressContainer, StyledPaidTagWrapper, StyledTooltipWrapper, StyledPaidTag, StyledFlexContent } from './path-card.styles';
export const PathCard = ({
  path,
  isCurrentPath,
  recommendedPath,
  progress,
  variant,
  isAddOn,
  isSmall
}: {
  path: (Path & {
    firstRoomCode: string;
  }) | undefined;
  isCurrentPath: boolean;
  recommendedPath?: string;
  progress: number;
  variant: RoadmapVariant;
  isAddOn?: boolean;
  isSmall?: boolean;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const {
    data: userData
  } = useGetUserQuery();
  const user = userData?.data?.user;
  const hasEnrolledInPath = !!userData?.data?.user?.local.path;
  const isRecommended = !hasEnrolledInPath || variant === 'welcome' && recommendedPath;
  const cardProps = isRecommended ? {
    isRecommendedPath: recommendedPath === path?.code
  } : {
    isCurrentPath
  };
  if (!path) {
    return null;
  }
  const handleStartLearning = () => {
    navigate(`/path-action/${path.code}/join?redirect=/room/${path.firstRoomCode}?auto-join=true`);
  };
  const handleCardClick = () => {
    if (!user) {
      navigate(`/path/outline/${path.code}`);
      return;
    }
    if (isCurrentPath && !isRecommended) {
      navigate('/paths');
      return;
    }
    setIsModalOpen(true);
  };
  const renderPathOutlineModal = () => <PathOutlineModal open={isModalOpen} pathCode={path.code} handleModalAction={handleStartLearning} handleCloseButtonAction={() => setIsModalOpen(false)} data-sentry-element="PathOutlineModal" data-sentry-component="renderPathOutlineModal" data-sentry-source-file="path-card.tsx" />;
  return <>
      <StyledPathCardContainer {...cardProps} onClick={handleCardClick} $isSmall={isSmall} data-sentry-element="StyledPathCardContainer" data-sentry-source-file="path-card.tsx">
        {isRecommended && recommendedPath === path?.code && <StyledCurrentPathTag>Recommended for you</StyledCurrentPathTag>}
        {isCurrentPath && !isRecommended && <StyledCurrentPathTag>Latest enrolled path</StyledCurrentPathTag>}
        <div style={{
        backgroundImage: `url(${path?.image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        width: '8rem',
        height: 'auto',
        borderTopLeftRadius: '0.5rem',
        borderBottomLeftRadius: '0.5rem'
      }} />
        <StyledPathCardContent flexDirection="column" alignItems="flex-start" data-sentry-element="StyledPathCardContent" data-sentry-source-file="path-card.tsx">
          <p>{path?.title}</p>
          <StyledFlexContent data-sentry-element="StyledFlexContent" data-sentry-source-file="path-card.tsx">
            <Difficulty isDarkVersion difficulty={path?.difficulty} hasIcon isSmall data-sentry-element="Difficulty" data-sentry-source-file="path-card.tsx" />
            <StyledPathTag data-sentry-element="StyledPathTag" data-sentry-source-file="path-card.tsx">Path</StyledPathTag>
            {isAddOn && <StyledPaidTagWrapper>
                <StyledTooltipWrapper>
                  <Tooltip arrowPosition="bottom-center">
                    This training comes at an additional cost and requires a separate payment for access.
                  </Tooltip>
                </StyledTooltipWrapper>
                <StyledPaidTag size="small">Add-on</StyledPaidTag>
              </StyledPaidTagWrapper>}
          </StyledFlexContent>
        </StyledPathCardContent>
        <StyledProgressContainer data-sentry-element="StyledProgressContainer" data-sentry-source-file="path-card.tsx">
          {progress === 100 && <StyledCheckIcon $cardType={CardTypeEnum.PATH} icon={faCheckCircle} data-testid="completed-icon" />}
          {progress > 0 && progress < 100 && <CircularProgress textColor={secondaryColors.grey[400]} progressValue={progress} size={30} />}
        </StyledProgressContainer>
      </StyledPathCardContainer>
      {renderPathOutlineModal()}
    </>;
};